import { FormEvent, useState } from 'react';
import InfoTip from './InfoTip';
import { deliveryConfigApi, formIssues, issueFor, toValidationIssues, ValidationIssue } from '../../services/deliveryConfigApi';
import DisclaimerField from './DisclaimerField';
import { RecoveryProvider, RecoveryProviderInput, RecoveryProviderType } from '../../types/delivery';
import {
  DEFAULT_DISCLAIMER_AR, DEFAULT_DISCLAIMER_EN, DISCLAIMER_MAX_LENGTH, INSURANCE_MESSAGES, MAX_INSURED_AMOUNT,
} from '../../utils/insuranceDisclaimer';

interface ProviderFormProps {
  /** null = add a new provider; otherwise the row being edited. */
  provider: RecoveryProvider | null;
  token: string;
  onClose: () => void;
  onSaved: (provider: RecoveryProvider) => void;
}

/** A contractor must say which it is; "" is the unset state that the form refuses to submit. */
type VatChoice = '' | 'REGISTERED' | 'NOT_REGISTERED';

const TRN_RE = /^\d{15}$/;

/**
 * Cost per trip is optional (AC-43 / #576): a contractor's price is agreed at month end and the
 * month's trips are paid in one payment, entered on the provider settlement's Pay form.
 */
export const COST_HINT =
  "Leave blank if the price is agreed at month end — you'll enter the invoiced total when you record the provider payment.";

const hintStyle = { fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' } as const;

function FieldError({ message, id }: { message: string | undefined; id: string }) {
  if (!message) return null;
  return (
    <div id={id} role="alert" style={{ fontSize: '0.75rem', color: 'var(--admin-danger)', marginTop: '0.25rem' }}>
      {message}
    </div>
  );
}

/**
 * Spec 053 Flow F6 / BR-27 / AC-41c — add or edit one recovery provider.
 *
 * Deliberately absent: drivers. A driver's name and phone are typed per assignment, so the registry
 * holds companies only. `costPerTrip` is optional for every provider, contractors included: a
 * contractor's price is usually agreed at month end and the whole month paid in one payment, so
 * blank means the trips accrue no cost and the invoiced total is entered when the provider
 * settlement is paid (AC-43). It is Garagat's cost, never shown to a customer.
 *
 * Also deliberately absent (#633): a "default" provider. The admin names the provider for every
 * Garagat leg when assigning its driver, so no row is ever pre-selected on their behalf.
 *
 * And (#1254) a transport type: every registry provider is a recovery truck. Zofeur is set up on the
 * Zofeur card (`ZofeurConfigForm`), which keeps its own provider row out of this registry.
 */
export default function ProviderForm({ provider, token, onClose, onSaved }: ProviderFormProps) {
  const editing = provider !== null;
  const [name, setName] = useState(provider?.name ?? '');
  const [type, setType] = useState<RecoveryProviderType>((provider?.type as RecoveryProviderType) ?? 'CONTRACTOR');
  // `vatRegistered` is a nullable Boolean on the API (an IN_HOUSE row, or one created before the
  // question was asked, carries null). Null is "nothing chosen", NOT "not VAT registered": pre-
  // selecting the latter would let a save silently record an answer the admin never gave.
  const declaredVat = provider?.vatRegistered as boolean | null | undefined;
  const [vatChoice, setVatChoice] = useState<VatChoice>(
    declaredVat === true ? 'REGISTERED' : declaredVat === false ? 'NOT_REGISTERED' : '',
  );
  const [trn, setTrn] = useState(provider?.trn ?? '');
  const [costPerTrip, setCostPerTrip] = useState(provider?.costPerTrip === null || provider?.costPerTrip === undefined ? '' : String(provider.costPerTrip));
  const [selfBillingOnFile, setSelfBillingOnFile] = useState(provider?.selfBillingOnFile ?? false);
  const [active, setActive] = useState(provider?.active ?? true);
  // Spec 072 — the Insurance section.
  const [insuredAmount, setInsuredAmount] = useState(
    provider?.insuredAmount === null || provider?.insuredAmount === undefined ? '' : String(provider.insuredAmount),
  );
  const [showDisclaimer, setShowDisclaimer] = useState(provider?.showInsuranceDisclaimer ?? false);
  const [disclaimerEn, setDisclaimerEn] = useState(provider?.disclaimerEn ?? '');
  const [disclaimerAr, setDisclaimerAr] = useState(provider?.disclaimerAr ?? '');

  const [saving, setSaving] = useState(false);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [error, setError] = useState<string | null>(null);

  const isContractor = type === 'CONTRACTOR';
  const parsedAmount = insuredAmount.trim() === '' ? null : Number(insuredAmount);
  const previewAmount = parsedAmount !== null && Number.isFinite(parsedAmount) ? parsedAmount : null;

  // Requirements §7: the defaults arrive the first time the toggle goes on with nothing written yet.
  const toggleDisclaimer = (on: boolean) => {
    setShowDisclaimer(on);
    if (on && !disclaimerEn.trim() && !disclaimerAr.trim()) {
      setDisclaimerEn(DEFAULT_DISCLAIMER_EN);
      setDisclaimerAr(DEFAULT_DISCLAIMER_AR);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setError(null);

    const local: ValidationIssue[] = [];
    if (!name.trim()) local.push({ field: 'name', message: 'Enter the provider name' });

    let cost: number | null = null;
    const rawCost = costPerTrip.trim();
    if (rawCost !== '') {
      const parsed = Number(rawCost);
      if (!Number.isFinite(parsed) || parsed < 0) {
        local.push({ field: 'costPerTrip', message: 'Enter a positive amount in AED, or leave blank to agree it at payment' });
      } else {
        cost = parsed;
      }
    }

    const cleanedTrn = trn.replace(/\s/g, '');
    if (isContractor) {
      if (vatChoice === '') {
        local.push({ field: 'trn', message: 'Give the contractor’s TRN, or state that they are not VAT registered' });
      } else if (vatChoice === 'REGISTERED' && !TRN_RE.test(cleanedTrn)) {
        local.push({ field: 'trn', message: 'A UAE TRN is 15 digits' });
      }
    }

    // Spec 072 §6 — the same rules the server answers 422 for, checked first so nothing is sent.
    let amount: number | null = null;
    if (insuredAmount.trim() !== '') {
      const parsed = Number(insuredAmount.trim());
      if (!Number.isInteger(parsed) || parsed < 0 || parsed > MAX_INSURED_AMOUNT) {
        local.push({ field: 'insuredAmount', message: INSURANCE_MESSAGES.amountRange });
      } else {
        amount = parsed;
      }
    }
    if (disclaimerEn.trim().length > DISCLAIMER_MAX_LENGTH || disclaimerAr.trim().length > DISCLAIMER_MAX_LENGTH) {
      local.push({ field: 'disclaimer', message: INSURANCE_MESSAGES.textLength });
    } else if (showDisclaimer && (!disclaimerEn.trim() || !disclaimerAr.trim())) {
      local.push({ field: 'disclaimer', message: INSURANCE_MESSAGES.textsRequired });
    }
    if (showDisclaimer && !local.some((i) => i.field === 'insuredAmount') && !(amount !== null && amount > 0)) {
      local.push({ field: 'insuredAmount', message: INSURANCE_MESSAGES.amountRequired });
    }

    if (local.length > 0) { setIssues(local); return; }

    const input: RecoveryProviderInput = {
      name: name.trim(),
      type,
      // Own fleet is Garagat itself: there is no supplier invoice and no self-billing agreement.
      trn: isContractor && vatChoice === 'REGISTERED' ? cleanedTrn : null,
      vatRegistered: isContractor && vatChoice === 'REGISTERED',
      costPerTrip: cost,
      selfBillingOnFile: isContractor ? selfBillingOnFile : false,
      active,
      insuredAmount: amount,
      showInsuranceDisclaimer: showDisclaimer,
      disclaimerEn: disclaimerEn.trim(),
      disclaimerAr: disclaimerAr.trim(),
    };

    setIssues([]);
    setSaving(true);
    try {
      const saved = provider
        ? await deliveryConfigApi.updateProvider(provider.id, input, token)
        : await deliveryConfigApi.createProvider(input, token);
      onSaved(saved);
    } catch (err) {
      const parsed = toValidationIssues(err);
      if (parsed.length > 0) setIssues(parsed);
      else setError(err instanceof Error ? err.message : 'Failed to save the provider');
    } finally {
      setSaving(false);
    }
  };

  const formLevel = formIssues(issues);

  return (
    <div className="admin-modal-overlay">
      <div className="admin-modal" role="dialog" aria-modal="true" aria-label={provider ? `Edit ${provider.name}` : 'Add recovery provider'} style={{ maxWidth: '520px', width: '100%' }}>
        <div className="admin-modal-header">
          <h3 style={{ margin: 0 }}>{editing ? 'Edit provider' : 'Add recovery provider'}</h3>
          <button type="button" className="admin-modal-close" onClick={onClose} disabled={saving} aria-label="Close">&times;</button>
        </div>

        <form onSubmit={submit}>
          <div className="admin-modal-body">
            {error && <div className="admin-info-banner danger" role="alert" data-testid="provider-error">{error}</div>}
            {formLevel.length > 0 && (
              <div className="admin-info-banner danger" role="alert" data-testid="provider-form-error">{formLevel.join(' ')}</div>
            )}

            <div className="admin-form-group">
              <label className="admin-form-label" htmlFor="provider-name">Provider name</label>
              <input
                id="provider-name"
                className="admin-form-input"
                value={name}
                disabled={saving}
                aria-invalid={!!issueFor(issues, 'name')}
                aria-describedby={issueFor(issues, 'name') ? 'provider-name-error' : undefined}
                onChange={(e) => setName(e.target.value)}
              />
              <FieldError id="provider-name-error" message={issueFor(issues, 'name')} />
            </div>

            <div className="admin-form-group">
              <label className="admin-form-label" id="provider-type-label">Type</label>
              <div role="radiogroup" aria-labelledby="provider-type-label" style={{ display: 'flex', gap: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                  <input type="radio" name="provider-type" checked={type === 'CONTRACTOR'} disabled={saving} onChange={() => setType('CONTRACTOR')} />
                  Contractor
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                  <input type="radio" name="provider-type" checked={type === 'IN_HOUSE'} disabled={saving} onChange={() => setType('IN_HOUSE')} />
                  Own fleet (in-house)
                </label>
              </div>
            </div>

            {isContractor ? (
              <div className="admin-form-group">
                <label className="admin-form-label" id="provider-vat-label">VAT status</label>
                <div role="radiogroup" aria-labelledby="provider-vat-label" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                    <input type="radio" name="provider-vat" checked={vatChoice === 'REGISTERED'} disabled={saving} onChange={() => setVatChoice('REGISTERED')} />
                    VAT registered — TRN
                  </label>
                  <input
                    id="provider-trn"
                    className="admin-form-input"
                    aria-label="TRN"
                    placeholder="15 digits"
                    inputMode="numeric"
                    value={trn}
                    disabled={saving || vatChoice !== 'REGISTERED'}
                    aria-invalid={!!issueFor(issues, 'trn')}
                    aria-describedby={issueFor(issues, 'trn') ? 'provider-trn-error' : undefined}
                    onChange={(e) => setTrn(e.target.value)}
                  />
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                    <input type="radio" name="provider-vat" checked={vatChoice === 'NOT_REGISTERED'} disabled={saving} onChange={() => setVatChoice('NOT_REGISTERED')} />
                    Not VAT registered
                  </label>
                </div>
                <FieldError id="provider-trn-error" message={issueFor(issues, 'trn')} />
              </div>
            ) : (
              <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginBottom: '1rem' }}>
                Own fleet is Garagat itself — no TRN, no supplier invoice and no self-billing agreement.
              </div>
            )}

            <div className="admin-form-group">
              <label className="admin-form-label" htmlFor="provider-cost">
                Cost per trip, ex. VAT (optional)
                <InfoTip glossaryKey="providerCostPerTrip" />
              </label>
              <input
                id="provider-cost"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                className="admin-form-input"
                placeholder="Leave blank to agree at payment"
                value={costPerTrip}
                disabled={saving}
                aria-invalid={!!issueFor(issues, 'costPerTrip')}
                aria-describedby={issueFor(issues, 'costPerTrip') ? 'provider-cost-hint provider-cost-error' : 'provider-cost-hint'}
                onChange={(e) => setCostPerTrip(e.target.value)}
              />
              <div id="provider-cost-hint" style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>
                {COST_HINT} Garagat&rsquo;s cost, never shown to customers.
              </div>
              <FieldError id="provider-cost-error" message={issueFor(issues, 'costPerTrip')} />
            </div>

            {isContractor && (
              <div className="admin-form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selfBillingOnFile} disabled={saving} onChange={(e) => setSelfBillingOnFile(e.target.checked)} />
                  Self-billing agreement on file
                </label>
              </div>
            )}

            <div className="admin-form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={active} disabled={saving} onChange={(e) => setActive(e.target.checked)} />
                Active — can be chosen when a trip is assigned
              </label>
            </div>

            <section
              data-testid="provider-insurance"
              aria-labelledby="provider-insurance-title"
              style={{ border: '1px solid var(--admin-border)', borderRadius: '8px', padding: '0.875rem 1rem', marginBottom: '1rem' }}
            >
              <h4 id="provider-insurance-title" style={{ margin: '0 0 0.75rem', fontSize: '0.875rem' }}>Insurance</h4>

              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="provider-insured-amount">Insured amount, AED (optional)</label>
                <input
                  id="provider-insured-amount"
                  data-testid="provider-insured-amount"
                  type="number"
                  step="1"
                  min="0"
                  max={MAX_INSURED_AMOUNT}
                  inputMode="numeric"
                  className="admin-form-input"
                  placeholder="e.g. 350000"
                  value={insuredAmount}
                  disabled={saving}
                  aria-invalid={!!issueFor(issues, 'insuredAmount')}
                  aria-describedby={issueFor(issues, 'insuredAmount') ? 'provider-insured-amount-error' : undefined}
                  onChange={(e) => setInsuredAmount(e.target.value)}
                />
                <FieldError id="provider-insured-amount-error" message={issueFor(issues, 'insuredAmount')} />
              </div>

              <div className="admin-form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    data-testid="provider-show-disclaimer"
                    checked={showDisclaimer}
                    disabled={saving}
                    onChange={(e) => toggleDisclaimer(e.target.checked)}
                  />
                  Show insurance disclaimer to customer
                </label>
                <div style={hintStyle}>
                  Shown in the app under the driver&rsquo;s details on trips this provider drives, once the
                  driver confirms. <code>{'{amount}'}</code> is filled in from the insured amount.
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                <DisclaimerField
                  idPrefix="provider"
                  lang="en"
                  label="Disclaimer (English)"
                  value={disclaimerEn}
                  amount={previewAmount}
                  disabled={saving}
                  invalid={!!issueFor(issues, 'disclaimer')}
                  onChange={setDisclaimerEn}
                />
                <DisclaimerField
                  idPrefix="provider"
                  lang="ar"
                  label="Disclaimer (Arabic)"
                  value={disclaimerAr}
                  amount={previewAmount}
                  disabled={saving}
                  invalid={!!issueFor(issues, 'disclaimer')}
                  onChange={setDisclaimerAr}
                />
              </div>
              <FieldError id="provider-disclaimer-error" message={issueFor(issues, 'disclaimer')} />
            </section>

            <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
              Changes apply to new assignments only. A trip already assigned keeps the provider,
              VAT status, cost and insurance disclaimer snapshotted at the time it was assigned.
            </div>
          </div>

          <div className="admin-modal-footer">
            <button type="button" className="admin-btn admin-btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save provider' : 'Add provider'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
